import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { DENTURE_ARCH_FDIS } from "@/lib/dentures";
import { multiToothKindOf } from "@/lib/procedureFlags";

function pesosFromCentavos(centavos) {
  return (Number(centavos || 0) / 100).toString();
}

export default function DentureTodayDialog({
  open,
  appointmentId,
  procedure,
  existingItemId = null,
  missingFdiSet = null,
  onClose,
  onStarted,
}) {
  const kind = multiToothKindOf(procedure);
  const isFull = kind === "full_denture";
  const autoMarksMissing = procedure?.is_extraction === true;
  const [arch, setArch] = useState(null);
  const [selectedTeeth, setSelectedTeeth] = useState([]);
  const [pricePesos, setPricePesos] = useState("0");
  const [submitting, setSubmitting] = useState(false);
  const missing = missingFdiSet instanceof Set ? missingFdiSet : new Set();
  const existingPriceLocked = !!existingItemId;

  useEffect(() => {
    if (!open) return;
    setArch(null);
    setSelectedTeeth([]);
    setPricePesos(pesosFromCentavos(procedure?.default_fee_centavos));
    setSubmitting(false);
  }, [open, procedure]);

  if (!open || !procedure || !["full_denture", "partial_denture"].includes(kind)) return null;

  const feeCentavos = Math.round(Number(pricePesos || 0) * 100);
  const validPrice = Number.isFinite(feeCentavos) && feeCentavos >= 0;
  const archTeeth = DENTURE_ARCH_FDIS[arch] || [];
  const archMidpoint = Math.floor(archTeeth.length / 2);
  const rightOfMidline = archTeeth.slice(0, archMidpoint);
  const leftOfMidline = archTeeth.slice(archMidpoint);

  function chooseArch(nextArch) {
    setArch(nextArch);
    setSelectedTeeth(isFull
      ? []
      : DENTURE_ARCH_FDIS[nextArch].filter((fdi) => missing.has(String(fdi))));
  }

  function toggleTooth(fdi) {
    setSelectedTeeth((current) => current.includes(fdi)
      ? current.filter((value) => value !== fdi)
      : [...current, fdi]);
  }

  async function submit() {
    if (!arch) return;
    if (!isFull && selectedTeeth.length < 1) {
      toast.error("Pick at least one tooth replaced by the partial denture.");
      return;
    }
    if (!validPrice) {
      toast.error("Enter a valid flat denture price.");
      return;
    }

    setSubmitting(true);
    const defaultFee = Number(procedure.default_fee_centavos || 0);
    const priceChanged = !existingPriceLocked && feeCentavos !== defaultFee;
    const scope = isFull ? [] : selectedTeeth.map((tooth_number) => ({ tooth_number }));
    const { data, error } = existingItemId
      ? await supabase.rpc("scope_calendar_denture_and_record", {
          p_item_id: existingItemId,
          p_appointment_id: appointmentId,
          p_arch: arch,
          p_teeth: scope,
          p_fee_centavos_override: null,
          p_override_reason: null,
        })
      : await supabase.rpc("record_denture_arch_today", {
          p_appointment_id: appointmentId,
          p_procedure_id: procedure.id,
          p_arch: arch,
          p_teeth: scope,
          p_fee_centavos_override: priceChanged ? feeCentavos : null,
          p_override_reason: priceChanged ? "Chairside denture flat price confirmed" : null,
        });
    setSubmitting(false);
    if (error) {
      toast.error(error.message || "Could not record the denture visit.");
      return;
    }
    toast.success(`${procedure.name} visit recorded for the ${arch} arch.`);
    onStarted?.(data);
  }

  function toothButton(fdi) {
    const selected = selectedTeeth.includes(fdi);
    const isMissing = missing.has(String(fdi));
    return (
      <button
        key={fdi}
        type="button"
        onClick={() => toggleTooth(fdi)}
        aria-pressed={selected}
        data-testid={`denture-tooth-${fdi}`}
        className={`h-11 min-w-0 w-full rounded-lg border text-xs font-semibold ${selected ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-violet-400"}`}
        title={isMissing ? `#${fdi} is already marked Missing` : `#${fdi} is present`}
      >
        {fdi}
        {isMissing ? <span className="block text-[8px] font-medium">missing</span> : null}
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="denture-today-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 backdrop-blur-sm p-4"
      data-testid="denture-today-dialog"
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <p id="denture-today-title" className="text-base font-semibold text-slate-900">
              Record {procedure.name} visit
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {isFull ? "Choose the jaw treated today." : "Choose one arch, then tap every tooth this partial replaces."}
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={submitting} aria-label="Exit denture recorder" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 disabled:opacity-40">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-5">
          {!arch ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                ["upper", "Upper Jaw", "FDI 18–28"],
                ["lower", "Lower Jaw", "FDI 48–38"],
              ].map(([value, label, hint]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => chooseArch(value)}
                  data-testid={`denture-arch-${value}`}
                  className="rounded-xl border-2 border-violet-200 bg-violet-50/60 px-5 py-8 text-left hover:border-violet-500 hover:bg-violet-50"
                >
                  <span className="block text-lg font-semibold text-violet-900">{label}</span>
                  <span className="block text-xs text-violet-600 mt-1">{hint}</span>
                </button>
              ))}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm font-semibold text-slate-800 capitalize">{arch} arch</div>
                <button type="button" onClick={() => chooseArch(arch === "upper" ? "lower" : "upper")} disabled={submitting} className="text-xs font-semibold text-violet-700 hover:underline">
                  Switch to {arch === "upper" ? "lower" : "upper"}
                </button>
              </div>

              {!isFull ? (
                <div className="rounded-xl border border-slate-200 p-4">
                  <p className="text-xs font-semibold text-slate-700 mb-3">Teeth replaced by this partial denture</p>
                  <div className="overflow-x-auto pt-4 pb-1">
                    <div
                      className="grid min-w-[600px] items-stretch gap-1.5"
                      style={{ gridTemplateColumns: "repeat(8, minmax(36px, 1fr)) 18px repeat(8, minmax(36px, 1fr))" }}
                    >
                      {rightOfMidline.map(toothButton)}
                      <div
                        className="relative flex min-h-11 items-stretch justify-center"
                        data-testid={`denture-midline-${arch}`}
                        aria-label={`${arch} arch midline`}
                      >
                        <span className="absolute -top-4 text-[8px] font-bold tracking-wider text-slate-500">MIDLINE</span>
                        <span aria-hidden className="w-0.5 rounded-full bg-slate-400" />
                      </div>
                      {leftOfMidline.map(toothButton)}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900">
                  This records today’s visit for the whole {arch} arch.
                </div>
              )}

              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
                {autoMarksMissing
                  ? (isFull
                    ? `Recording this visit will mark the ${arch} arch teeth Missing on the patient chart.`
                    : "Recording this visit will mark the selected replacement teeth Missing on the patient chart.")
                  : "This service is not set to mark teeth Missing. Update Settings > Services or chart the teeth as Missing before installation."}
              </div>

              {existingPriceLocked ? (
                <div className="block" data-testid="denture-agreed-price">
                  <span className="block text-xs font-semibold text-slate-700 mb-1">Agreed denture price</span>
                  <div className="flex max-w-xs items-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                    <span className="px-3 py-2 text-sm text-slate-500">₱</span>
                    <span className="px-3 py-2 text-sm font-semibold text-slate-800">{pricePesos}</span>
                    <span className="ml-auto px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">Locked</span>
                  </div>
                  <span className="block text-[11px] text-slate-500 mt-1">This is the accepted package price. Adding clinical scope does not change it.</span>
                </div>
              ) : (
                <label className="block">
                  <span className="block text-xs font-semibold text-slate-700 mb-1">Flat denture price</span>
                  <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden max-w-xs focus-within:border-violet-400">
                    <span className="px-3 py-2 bg-slate-50 text-sm text-slate-500">₱</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={pricePesos}
                      onChange={(event) => setPricePesos(event.target.value)}
                      data-testid="denture-flat-price"
                      className="w-full px-3 py-2 text-sm outline-none"
                    />
                  </div>
                  <span className="block text-[11px] text-slate-500 mt-1">One price for this denture—never multiplied by the number of teeth.</span>
                </label>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
          <button type="button" onClick={onClose} disabled={submitting} className="px-3 py-2 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg disabled:opacity-40">
            Exit
          </button>
          {arch ? (
            <button
              type="button"
              onClick={submit}
              disabled={submitting || (!isFull && selectedTeeth.length < 1) || !validPrice}
              data-testid="denture-record-visit"
              className="px-4 py-2.5 rounded-lg bg-violet-600 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Record today’s visit
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
